import { Group, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { type UseQueryExecute, useMutation } from "urql";
import { DEFAULT_IMAGE_QUALITY, DEFAULT_IMAGE_SIZE, IMAGE_QUALITY_OPTIONS, IMAGE_SIZE_OPTIONS } from "../utils";
import PromptInput from "./PromptInput";
import {
    type ImageThreadCreateMutation,
    type ImageThreadCreateMutationVariables,
    imageThreadCreateMutation,
} from "./queries";

interface FormValues {
    initialText: string;
    size: string;
    quality: string;
}

interface IProps {
    refetch?: UseQueryExecute;
}

export default function CreateThreadForm({ refetch }: IProps): JSX.Element {
    const navigate = useNavigate();
    const [result, executeCreate] = useMutation<ImageThreadCreateMutation, ImageThreadCreateMutationVariables>(
        imageThreadCreateMutation,
    );

    const form = useForm<FormValues>({
        initialValues: { initialText: "", size: DEFAULT_IMAGE_SIZE, quality: DEFAULT_IMAGE_QUALITY },
        validate: { initialText: (v) => (v.trim() ? null : "Required") },
    });

    const submit = async (values: FormValues) => {
        const res = await executeCreate({
            initialText: values.initialText,
            size: values.size || undefined,
            quality: values.quality || undefined,
        });
        if (res.error) {
            notifications.show({ title: "Failed to create thread", message: res.error.message });
            return;
        }
        const id = res.data!.imageThreadCreate.id;
        form.reset();
        // Refresh the threads list so it shows the new thread immediately
        refetch?.({ requestPolicy: "network-only" });
        navigate(`/image-threads/${id}`);
    };

    return (
        <Stack>
            <PromptInput
                placeholder="Describe the image to create..."
                {...form.getInputProps("initialText")}
                submitting={result.fetching}
                onSubmit={form.onSubmit(submit)}
            />
            <Group gap="sm">
                <Select
                    placeholder="Size"
                    data={[...IMAGE_SIZE_OPTIONS]}
                    size="xs"
                    w={200}
                    clearable
                    {...form.getInputProps("size")}
                />
                <Select
                    placeholder="Quality"
                    data={[...IMAGE_QUALITY_OPTIONS]}
                    size="xs"
                    w={160}
                    clearable
                    {...form.getInputProps("quality")}
                />
            </Group>
        </Stack>
    );
}
